import { type AppBus, type AppEvents, BUS_SUBSCRIBER_POLICY } from '@/services/AppBus';
import { InvariantError } from '@/domain/errors';

// Events that must have a subscriber wired at boot. The bus drops a zero-handler publish
// silently, and the container is lazy, so a subscriber nobody eager-resolves never subscribes and
// the event vanishes with no error. This list turns that silent drop into a loud boot failure.
//
// Derived from BUS_SUBSCRIBER_POLICY so the boot check and the BusNarrator's runtime
// zero-handler detection share one compiler-owned classification.
export const BOOT_REQUIRED_SUBSCRIBERS: readonly (keyof AppEvents)[] = (
  Object.keys(BUS_SUBSCRIBER_POLICY) as (keyof AppEvents)[]
).filter((event) => BUS_SUBSCRIBER_POLICY[event] === 'required');

// Throws if any of `events` has no subscriber on the bus. Call once at boot, after the eager
// resolves and before anything starts publishing. The error names the unwired events so it points
// at the missing line.
//
// narratorHandlers: when the BusNarrator resolves before this check (main.tsx does, so
// constructor-time publishes are narrated), its subscription inflates every count by one; pass 1
// so an event whose only listener is the narrator still fails loud. Either misordering is caught:
// narrator-after-check with offset 1 throws (store counts alone do not clear the bar), and the
// offset lives next to the narrator resolve in main.tsx so the two move together.
export function assertBootSubscribers(
  bus: AppBus,
  events: readonly (keyof AppEvents)[] = BOOT_REQUIRED_SUBSCRIBERS,
  narratorHandlers = 0,
): void {
  const missing = events.filter((event) => bus.handlerCount(event) <= narratorHandlers);
  if (missing.length > 0) {
    throw new InvariantError(
      `Boot subscriber check failed: no handler registered for ${missing.join(', ')}. ` +
        `A singleton that subscribes to the bus must be eager-resolved in main.tsx before ` +
        `anything publishes (see INVARIANTS rule 20).`,
    );
  }
}
